import { Box } from "@primer/react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { crmHocSinhApi } from "../../../api/crmHocSinhApi";
import { quanHeGiaDinhApi } from "../../../api/quanHeGiaDinhApi";
import Button from "../../../components/ui/button";
import ModalActions from "../../../components/ui/modal/ModalActions";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { HocSinhFormSubmitRequest } from "../../../models/request/hoc-sinh/HocSinhFormSubmitRequest";
import { IHocSinh } from "../../../models/response/crm-hocsinh/IHocsinh";
import ThongTinDangKy from "./ThongTinDangKy";
import ThongTinDiaChi from "./ThongTinDiaChi";
import ThongTinHocSinh from "./ThongTinHocSinh";
import ThongTinKhac from "./ThongTinKhac";
import ThongTinLienHe from "./ThongTinLienHe";

interface PhuHuynh {
  ho_ten?: string;
  dien_thoai?: string;
  email?: string;
  dm_quanhe_id?: number;
}

interface ProfileFormProps {
  hocSinh: IHocSinh;
  phuHuynh?: PhuHuynh;
  onSubmitSuccess: (id: number) => void;
  height?: number;
}

interface FormRef {
  handleSaveChanges: () => void;
}


const ProfileForm = forwardRef<FormRef, ProfileFormProps>(
  ({ hocSinh, onSubmitSuccess, phuHuynh, height }, ref) => {
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
    const { translate } = useCommonContext();
    const [selectedProvince, setSelectedProvince] = useState<any>(
      hocSinh.dm_tinh_id || null
    );
    const [selectedDistrict, setSelectedDistrict] = useState<any>(
      hocSinh.dm_huyen_id || null
    );
    const [selectedCommune, setSelectedCommune] = useState<any>(
      hocSinh.dm_xa_id || null
    );

    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState<IHocSinh>({ ...hocSinh });
    const [generatedCode, setGeneratedCode] = useState<string>("");
    const [primaryContact, setPrimaryContact] = useState<{
        ho_ten?: string;
        dien_thoai?: string;
        email?: string;
        dm_quanhe_id?: number;
        quan_he?: string;
    }>({});
    const {
      register,
      handleSubmit,
      control,
      trigger,
      setValue,
      formState: { errors },
    } = useForm<IHocSinh>({ mode: "all", defaultValues: formData });

    // Function để sinh mã tiềm năng
    const generatePotentialCode = useCallback(async () => {
      try {
        const response = await crmHocSinhApi.generateStudentCode(dm_coso_id);
        if (response.is_success && response.data) {
          setGeneratedCode(response.data);
          // Gán vào ma_tn thay vì ma_hs
          setValue("ma_tn", response.data);
          setFormData((prev) => ({
            ...prev,
            ma_tn: response.data,
          }));
        }
      } catch (error) {
        console.error("Error generating potential code:", error);
      }
    }, [dm_coso_id, setValue, setFormData, setGeneratedCode]);

    useEffect(() => {
      if (hocSinh) {
        let _data = {
          ...hocSinh,
          nam_hoc: nam_hoc,
          dm_coso_id: dm_coso_id,
          ngay_sinh: hocSinh.ngay_sinh ? new Date(hocSinh.ngay_sinh) : null,
          ngay_nhap_hoc: hocSinh.ngay_nhap_hoc ? new Date(hocSinh.ngay_nhap_hoc) : null,
          ngaythi_dauvao_dukien: hocSinh.ngaythi_dauvao_dukien ? new Date(hocSinh.ngaythi_dauvao_dukien) : null,
        };
        setFormData(_data);

        // Update form values for the comboboxes
        if (hocSinh.dm_truong_id) {
          setValue("dm_truong_id", hocSinh.dm_truong_id);
        }
        if (hocSinh.dm_khoi_id) {
          setValue("dm_khoi_id", hocSinh.dm_khoi_id);
        }
        if (hocSinh.dm_he_id) {
          setValue("dm_he_id", hocSinh.dm_he_id);
        }
        if (hocSinh.dm_lop_id) {
          setValue("dm_lop_id", hocSinh.dm_lop_id);
        }

        // Nếu là thêm mới (id = 0) thì sinh mã tiềm năng
        if (hocSinh.id === 0) {
          generatePotentialCode();
        }
      }
    }, [hocSinh, nam_hoc, dm_coso_id, setValue, generatePotentialCode]);

    // Load quan hệ gia đình để tìm liên hệ chính
    useEffect(() => {
      const loadPrimaryContact = async () => {
        if (hocSinh.id > 0) {
          try {
            const res = await quanHeGiaDinhApi.selectByHocSinh(hocSinh.id);
            if (res.is_success && res.data) {
              // Tìm quan hệ có is_uutien = true
              const primaryRelation = res.data.find((relation: any) => relation.is_uutien);
              if (primaryRelation && primaryRelation.ts_phuhuynh) {
                setPrimaryContact({
                  ho_ten: primaryRelation.ts_phuhuynh.ho_ten,
                  dien_thoai: primaryRelation.ts_phuhuynh.dien_thoai,
                  email: primaryRelation.ts_phuhuynh.email,
                  dm_quanhe_id: primaryRelation.dm_quanhe_id,
                  quan_he: primaryRelation.ts_phuhuynh.quan_he,
                });
              } else {
                setPrimaryContact({});
              }
            }
          } catch (error) {
            console.error("Error loading primary contact:", error);
            setPrimaryContact({});
          }
        } else {
          setPrimaryContact({});
        }
      };

      loadPrimaryContact();
    }, [hocSinh.id]);

    const handleProvinceChange = (value: any) => {
      setSelectedProvince(value);
      setSelectedDistrict(null);
      setSelectedCommune(null);
      setFormData((prev) => ({
        ...prev,
        dm_tinh_id: value,
        dm_huyen_id: 0,
        dm_xa_id: 0,
      }));
    };
    const handleDistrictChange = (value: any) => {
      setSelectedDistrict(value);
      setSelectedCommune(null);
      setFormData((prev) => ({
        ...prev,
        dm_huyen_id: value,
        dm_xa_id: 0,
      }));
    };
    const handleCommuneChange = (value: any) => {
      setSelectedCommune(value);
      setFormData((prev) => ({
        ...prev,
        dm_xa_id: value,
      }));
    };

    const handleKhoiChange = (value: number) => {
      setFormData((prev) => ({
        ...prev,
        dm_khoi_id: value,
        dm_he_id: 0,
        dm_lop_id: 0,
      }));
      setValue("dm_he_id", 0);
      setValue("dm_lop_id", 0);
    };


    const handleSaveChanges = async (data: any) => {
      setIsSaving(true);

      // Không tự sinh/gửi ma_hs khi thêm mới. Chỉ dùng ma_tn từ generatedCode
      const finalMaTn = data.id === 0 ? (generatedCode || data.ma_tn || "") : (data.ma_tn || "");

      // Helper function to format date to YYYY-MM-DD string to avoid timezone issues
      const formatDateForBackend = (date: Date | null | undefined): string | null => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Helper function to format date to timestamp to avoid timezone issues
      const formatDateToTimestamp = (date: Date | null | undefined): number => {
        if (!date) return 0;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = d.getMonth();
        const day = d.getDate();
        // Create UTC date to avoid timezone issues
        return new Date(Date.UTC(year, month, day)).getTime();
      };

      const formDataSubmit: HocSinhFormSubmitRequest = {
        id: data.id,
        ma_hs: data.ma_hs ?? "", // không sinh ở frontend; giữ để backend bỏ qua khi tạo mới
        ma_tn: finalMaTn,
        nam_hoc: data.nam_hoc ?? nam_hoc,
        ho_ten: data.ho_ten ?? "",
        ho_ten_en: data.ho_ten_en ?? "",
        ngay_sinh_timestamp: formatDateToTimestamp(data.ngay_sinh),
        dm_gioitinh_id: data.dm_gioitinh_id,
        dia_chi_noi_sinh: data.dia_chi_noi_sinh ?? "",
        truong_hoc_cu: data.truong_hoc_cu ?? "",
        ho_ten_lien_he: data.ho_ten_lien_he ?? "",
        dien_thoai_lien_he: data.dien_thoai_lien_he ?? "",
        email_lien_he: data.email_lien_he ?? "",
        dm_chiendich_id: data.dm_chiendich_id,
        dm_quanhe_id: data.dm_quanhe_id,
        dm_nguonbiettoi_id: data.dm_nguonbiettoi_id,
        dm_tinh_id: data.dm_tinh_id,
        dm_huyen_id: data.dm_huyen_id,
        dm_xa_id: data.dm_xa_id,
        dia_chi_tt: data.dia_chi_tt ?? "",
        dm_truong_id: data.dm_truong_id,
        dm_khoi_id: data.dm_khoi_id || formData.dm_khoi_id,
        dm_lop_id: data.dm_lop_id || formData.dm_lop_id,
        dm_he_id: data.dm_he_id,
        dm_quoctich_id: data.dm_quoctich_id,
        dm_coso_id: data.dm_coso_id ?? dm_coso_id,
        ngaythi_dauvao_dukien: formatDateForBackend(data.ngaythi_dauvao_dukien) as any,
        ngay_nhap_hoc: formatDateForBackend(data.ngay_nhap_hoc) as any,
        que_quan: data.que_quan ?? "",
        nam_nhap_hoc: data.nam_nhap_hoc ?? "",
        email_hocsinh: data.email_hocsinh ?? "",
        dien_thoai_hocsinh: data.dien_thoai_hocsinh ?? "",
        ma_so_bhxh: data.ma_so_bhxh ?? "",
        loai_dang_ky_bhxh: data.loai_dang_ky_bhxh ?? "",
        so_tien_bhxh: data.so_tien_bhxh ?? 0,
        noi_dang_ky_kcb_bandau: data.noi_dang_ky_kcb_bandau ?? "",
        doi_tuong_chinh_sach: data.doi_tuong_chinh_sach ?? "",
        ma_cua_bo: data.ma_cua_bo ?? "",
        chung_minh_thu_nguoilienhe: "",
        noi_cap_chung_minh_thu_nguoilienhe: "",
        ngay_cap_chung_minh_thu_nguoilienhe: undefined,
        ngay_sinh_nguoilienhe: undefined,
        quoc_tich_id_nguoilienhe: data.dm_quoctich_id,
        nghe_nghiep_nguoilienhe: "",
        noi_lam_viec_nguoilienhe: "",
        dia_chi_thuong_tru_nguoilienhe: "",
        dm_dantoc_id: data.dm_dantoc_id,
        dm_tongiao_id: data.dm_tongiao_id,
        ma_dinh_danh: data.ma_dinh_danh ?? "",
        tt_suc_khoe: data.tt_suc_khoe ?? "",
        dia_chi_hientai: data.dia_chi_hientai ?? "",
        chieu_cao: data.chieu_cao ?? "",
        can_nang: data.can_nang ?? "",
        so_dien_thoai_bo: "",
        so_dien_thoai_me: "",
        ho_chieu: data.ho_chieu ?? "",
        ngay_ketthuc_hoc: data.ngay_ketthuc_hoc ?? undefined,
        ma_dinh_danh_csdl: data.ma_dinh_danh_csdl ?? "",
        ma_so_bhyt: data.ma_so_bhyt ?? "",
        ghi_chu_chung: data.ghi_chu_chung ?? '',

        dm_trangthaihocsinh_id: data.dm_trangthaihocsinh_id ?? 0,
        is_nhucau_noitru: data.is_nhucau_noitru ?? false,
        uy_quyen_cho_truong: data.uy_quyen_cho_truong ?? false,
        is_nhucau_dangky_bus: (data as any).is_nhucau_dangky_bus ?? false,
        is_dangky_ansang: (data as any).is_dangky_ansang ?? false,
        laconthu: data.laconthu ?? 1

      };
      console.log("formDataSubmit:", formDataSubmit);

      const isValid = await trigger();
      if (!isValid) {
        setIsSaving(false);
        return;
      }

      let res;
      if (formData.id > 0) {
        // Cập nhật học sinh
        res = await crmHocSinhApi.update(formDataSubmit);
      } else {
        // Thêm mới học sinh - Backend sẽ tự động sinh mã mới
        res = await crmHocSinhApi.insert(formDataSubmit);
      }

      if (res.is_success) {
        NotifyHelper.Success(
          formData.id > 0 ? "Cập nhật thành công" : "Thêm mới thành công"
        );
        onSubmitSuccess(res.data);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
      setIsSaving(false);
    };

    return (


      <form onSubmit={handleSubmit(handleSaveChanges)}>
        <Box sx={{
          display: "grid",
          gap: "24px",
          position: "relative",
        }}>
          <ThongTinHocSinh
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}

          />
          <ThongTinDangKy
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}

          />
          <ThongTinDiaChi
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
          />
          <ThongTinKhac
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
          />
          <ThongTinLienHe
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            generatedCode={generatedCode}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
            primaryContact={primaryContact}
          />
          <Box sx={{
            // backgroundColor: "rgba(0,0,0,0.1)",
            backdropFilter: "blur(1px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            position: "sticky",
            bottom: 0,
            // borderRadius: 2,
          }}>

            <Button
              text="Base.Label.Save"
              variant="primary"
              type="submit"
              size="large"
              block
              isLoading={isSaving}
              sx={{
                width: "250px",
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px;",
                borderRadius:"32px",
              }}
            />

          </Box>
        </Box>

      </form>


    );
  }
);

export default ProfileForm;
